<template>
    <div>
        <!-- Botón para abrir el modal -->
        <slot name="trigger" :open="openModal">
            <ArgonButton variant="gradient" color="info" size="md" class="w-100" @click="openModal">
                <i class="fas fa-file-code me-2"></i>
                {{ label }}
            </ArgonButton>
        </slot>

        <!-- Modal -->
        <teleport to="body">
            <transition name="modal-fade">
                <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
                    <div class="modal-container">
                        <!-- Header del modal -->
                        <div class="modal-header">
                            <div class="header-left">
                                <div class="icon-wrapper">
                                    <i class="fas fa-file-code"></i>
                                </div>
                                <div>
                                    <h4 class="modal-title">Selector de Plantillas</h4>
                                    <p class="modal-subtitle">Busca, agrega o edita plantillas HTML</p>
                                </div>
                            </div>
                            <button class="close-btn" @click="closeModal">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>

                        <!-- Barra de búsqueda y filtros -->
                        <div v-if="!showForm" class="search-section">
                            <div class="search-wrapper">
                                <i class="fas fa-search search-icon"></i>
                                <input
v-model="searchQuery" placeholder="Buscar plantillas..." class="search-input"
                                    autofocus />
                                <transition name="fade">
                                    <button
v-if="searchQuery || selectedLetter" class="clear-search-btn"
                                        @click="clearSearch">
                                        <i class="fas fa-times"></i>
                                    </button>
                                </transition>
                            </div>

                            <!-- Alfabeto -->
                            <div class="alphabet-section">
                                <div class="alphabet-buttons">
                                    <button
v-for="letter in alphabet" :key="letter"
                                        :class="['alphabet-btn', { 'active': selectedLetter === letter }]"
                                        @click="filterByLetter(letter)">
                                        {{ letter }}
                                    </button>
                                </div>
                            </div>

                            <!-- Botón para mostrar formulario -->
                            <div v-if="canEditAndAdd" class="show-form-section">
                                <ArgonButton
variant="gradient" color="info" size="md" class="w-100"
                                    @click="showFormHandler">
                                    <i class="fas fa-plus-circle me-2"></i>
                                    Agregar Nueva Plantilla
                                </ArgonButton>
                            </div>
                        </div>

                        <!-- Lista de plantillas -->
                        <div v-if="!showForm" class="templates-section">
                            <div v-if="templates.length === 0 && !isLoadingMore" class="empty-state">
                                <i class="fas fa-file-code empty-icon"></i>
                                <p class="empty-text">No se encontraron plantillas</p>
                                <p class="empty-subtext">Prueba con otra búsqueda o agrega una nueva</p>
                            </div>

                            <div v-else ref="scrollableOptions" class="templates-list" @scroll="onScroll">
                                <transition-group name="list-fade" tag="div">
                                    <div
v-for="item in templates" :key="item.id"
                                        :class="['template-card', { 'selected': selectedTemplateId === item.id }]">
                                        <div class="template-content" @click="selectTemplate(item)">
                                            <div class="template-name">
                                                <i class="fas fa-file-code me-2"></i>
                                                <strong>{{ item.name }}</strong>
                                            </div>
                                            <div class="template-preview" v-html="getPreview(item.content)"></div>
                                        </div>
                                        <div class="template-actions">
                                            <button
class="action-btn use-btn" title="Usar plantilla"
                                                @click.stop="useTemplate(item)">
                                                <i class="fas fa-check"></i>
                                            </button>
                                            <button
v-if="canEditAndAdd" class="action-btn edit-btn" title="Editar"
                                                @click.stop="editTemplate(item)">
                                                <i class="fas fa-edit"></i>
                                            </button>
                                            <button
v-if="canEditAndAdd" class="action-btn delete-btn" title="Eliminar"
                                                @click.stop="handleDelete(item.id)">
                                                <i class="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                    </div>
                                </transition-group>
                                <div v-if="isLoadingMore" class="loading-more">
                                    <i class="fas fa-spinner fa-spin me-2"></i>
                                    Cargando más...
                                </div>
                            </div>
                        </div>

                        <!-- Formulario de agregar/editar -->
                        <transition name="slide-fade">
                            <div v-if="showForm && canEditAndAdd" class="form-section-full">
                                <div class="form-header-full">
                                    <div class="form-header-left">
                                        <i :class="isEditing ? 'fas fa-edit' : 'fas fa-plus-circle'" class="me-2"></i>
                                        {{ isEditing ? 'Editar Plantilla' : 'Agregar Nueva Plantilla' }}
                                    </div>
                                    <button class="back-btn" @click="hideFormHandler">
                                        <i class="fas fa-arrow-left me-2"></i>
                                        Volver al Listado
                                    </button>
                                </div>
                                <div class="form-content">
                                    <div class="form-grid">
                                        <div class="form-group full-width">
                                            <label class="form-label">
                                                <i class="fas fa-tag me-1"></i>
                                                Nombre de la Plantilla
                                                <span class="required-star">*</span>
                                            </label>
                                            <input
v-model="newTemplateName" placeholder="Ej: Receta Estándar"
                                                class="form-input" autofocus />
                                        </div>
                                        <div class="form-group full-width">
                                            <label class="form-label">
                                                <i class="fas fa-code me-1"></i>
                                                Contenido de la Plantilla
                                                <span class="required-star">*</span>
                                            </label>
                                            <div class="editor-wrapper-modal">
                                                <quill-editor
ref="templateEditorRef" v-model:value="localEditorContent"
                                                    :options="editorOptions" class="editor-modal" />
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-actions">
                                        <ArgonButton
variant="gradient" :color="isEditing ? 'warning' : 'info'"
                                            size="lg" :disabled="!newTemplateName.trim() || !localEditorContent.trim()"
                                            @click="isEditing ? updateTemplate() : addTemplate()">
                                            <i :class="isEditing ? 'fas fa-save' : 'fas fa-check'" class="me-2"></i>
                                            {{ isEditing ? 'Guardar Cambios' : 'Agregar Plantilla' }}
                                        </ArgonButton>
                                        <ArgonButton
variant="outline" color="secondary" size="lg"
                                            @click="hideFormHandler">
                                            <i class="fas fa-times me-2"></i>
                                            Cancelar
                                        </ArgonButton>
                                    </div>
                                </div>
                            </div>
                        </transition>

                        <!-- Footer del modal -->
                        <div class="modal-footer">
                            <ArgonButton variant="outline" color="secondary" size="md" @click="closeModal">
                                <i class="fas fa-times me-2"></i>
                                Cerrar
                            </ArgonButton>
                        </div>
                    </div>
                </div>
            </transition>
        </teleport>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, getCurrentInstance, computed } from 'vue'
import api from '@/services/api'
import ArgonButton from '@/components/ArgonButton.vue'
import Swal from 'sweetalert2'
import { quillEditor } from 'vue3-quill'

const { proxy } = getCurrentInstance()

const props = defineProps({
    label: {
        type: String,
        default: 'Usar Plantilla'
    },
    apiurl: {
        type: String,
        default: 'html-templates'
    },
    canEditAndAdd: {
        type: Boolean,
        default: true
    },
    editorContent: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['template-selected', 'update:editor-content'])

// Estado del modal
const isModalOpen = ref(false)
const showForm = ref(false)

// Datos
const allTemplates = ref([])
const templates = ref([])
const selectedTemplateId = ref(null)
const searchQuery = ref('')
const selectedLetter = ref('')

// Formulario
const newTemplateName = ref('')
const localEditorContent = ref('')
const isEditing = ref(false)
const editingTemplateId = ref(null)
const templateEditorRef = ref(null)

// Paginación
const isLoadingMore = ref(false)
const currentPage = ref(1)
const itemsPerPage = ref(100)
const totalPages = ref(1)
const scrollableOptions = ref(null)

// Alfabeto
const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

// Opciones del editor Quill
const editorOptions = computed(() => {
    return proxy.$editorOptions || {
        theme: 'snow',
        modules: {
            toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'align': [] }],
                ['clean']
            ]
        }
    }
})

// Funciones de utilidad
const normalizeString = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()

const normalizeData = (data) => {
    return data.map(item => ({
        id: item.id,
        name: item.name,
        content: item.content,
        normalizedName: normalizeString(item.name),
        deleted_at: item.deleted_at || null,
    }))
}

const saveToLocalStorage = (data) => {
    localStorage.setItem('html-templates', JSON.stringify(data))
}

const loadFromLocalStorage = () => {
    const data = localStorage.getItem('html-templates')
    return data ? JSON.parse(data) : []
}

const getPreview = (html) => {
    if (!html) return 'Sin contenido'
    const temp = document.createElement('div')
    temp.innerHTML = html
    const text = temp.textContent.trim()
    return text.substring(0, 100) + (text.length > 100 ? '...' : '')
}

// Modal
const openModal = async () => {
    isModalOpen.value = true
    document.body.style.overflow = 'hidden'

    if (allTemplates.value.length === 0) {
        allTemplates.value = loadFromLocalStorage()
    }

    updateTemplates()

    if (allTemplates.value.length === 0) {
        await fetchAllTemplates()
    }
}

const closeModal = () => {
    isModalOpen.value = false
    showForm.value = false
    document.body.style.overflow = ''
    resetEditState()
    searchQuery.value = ''
    selectedLetter.value = ''
}

// Mostrar/ocultar formulario
const showFormHandler = () => {
    showForm.value = true
    resetEditState()
}

const hideFormHandler = () => {
    showForm.value = false
    resetEditState()
}

// Búsqueda y filtros
const clearSearch = () => {
    searchQuery.value = ''
    selectedLetter.value = ''
    fetchAllTemplates(1)
}

const filterByLetter = (letter) => {
    selectedLetter.value = letter
    searchQuery.value = ''
    fetchAllTemplates(1, letter)
}

// API
const fetchAllTemplates = async (page = 1, firstLetter = '') => {
    try {
        isLoadingMore.value = true
        let url = `/${props.apiurl}?page=${page}&per_page=${itemsPerPage.value}`
        if (searchQuery.value) {
            url += `&search=${normalizeString(searchQuery.value)}`
        } else if (firstLetter) {
            url += `&first_letter=${firstLetter}`
        }

        const response = await api.get(url)
        const newItems = normalizeData(response.data.data)
        allTemplates.value = page === 1 ? newItems : [...allTemplates.value, ...newItems]

        saveToLocalStorage(allTemplates.value)
        totalPages.value = response.data.pagination.last_page

        updateTemplates()
    } catch (error) {
        console.error('Error fetching templates:', error)
        proxy.$notify('Error al cargar plantillas', '', 'danger', 'ni ni-fat-remove ni-lg')
    } finally {
        isLoadingMore.value = false
    }
}

const updateTemplates = () => {
    const filteredItems = allTemplates.value.filter(item => {
        const notDeleted = item.deleted_at === null || item.deleted_at === undefined
        const matchesSearch = !searchQuery.value || item.normalizedName.includes(normalizeString(searchQuery.value))
        return notDeleted && matchesSearch
    })
    templates.value = filteredItems
}

// Selección y uso
const selectTemplate = (item) => {
    selectedTemplateId.value = item.id
}

const useTemplate = (item) => {
    emit('template-selected', { name: item.name, content: item.content })
    proxy.$notify(`${item.name} agregada`, '', 'success', 'ni ni-like-2 ni-lg')
    closeModal()
}

// CRUD
const addTemplate = async () => {
    if (!newTemplateName.value.trim() || !localEditorContent.value.trim()) return

    let existingItem = allTemplates.value.find(item =>
        item.normalizedName === normalizeString(newTemplateName.value.trim())
    )

    if (existingItem) {
        if (existingItem.deleted_at !== null) {
            try {
                await api.post(`/${props.apiurl}/${existingItem.id}/restore`)
                existingItem.deleted_at = null
                saveToLocalStorage(allTemplates.value)
            } catch (error) {
                console.error('Error restoring template:', error)
            }
        }
        useTemplate(existingItem)
        resetEditState()
        showForm.value = false
        return
    }

    try {
        const response = await api.post(`/${props.apiurl}`, {
            name: newTemplateName.value,
            content: localEditorContent.value
        })
        const newItem = response.data.data
        allTemplates.value.push(normalizeData([newItem])[0])
        allTemplates.value.sort((a, b) => a.normalizedName.localeCompare(b.normalizedName))
        saveToLocalStorage(allTemplates.value)

        useTemplate(newItem)
        resetEditState()
        updateTemplates()
        proxy.$notify('Plantilla creada y agregada', '', 'success', 'ni ni-like-2 ni-lg')
        showForm.value = false
    } catch (error) {
        console.error('Error adding template:', error)
        proxy.$notify('Error al agregar plantilla', '', 'danger', 'ni ni-fat-remove ni-lg')
    }
}

const editTemplate = (item) => {
    newTemplateName.value = item.name
    localEditorContent.value = item.content
    isEditing.value = true
    editingTemplateId.value = item.id
    showForm.value = true
}

const updateTemplate = async () => {
    if (!newTemplateName.value.trim() || !localEditorContent.value.trim()) return

    try {
        const response = await api.put(`/${props.apiurl}/${editingTemplateId.value}`, {
            name: newTemplateName.value,
            content: localEditorContent.value
        })
        const updatedItem = response.data.data
        const index = allTemplates.value.findIndex(item => item.id === editingTemplateId.value)
        if (index !== -1) {
            allTemplates.value[index] = normalizeData([updatedItem])[0]
        }

        saveToLocalStorage(allTemplates.value)
        resetEditState()
        updateTemplates()
        proxy.$notify('Plantilla actualizada exitosamente', '', 'success', 'ni ni-like-2 ni-lg')
        showForm.value = false
    } catch (error) {
        console.error('Error updating template:', error)
        proxy.$notify('Error al actualizar plantilla', '', 'danger', 'ni ni-fat-remove ni-lg')
    }
}

const handleDelete = async (id) => {
    const template = templates.value.find(item => item.id === id)
    if (!template) return

    const result = await Swal.fire({
        title: '¿Estás seguro?',
        html: `¡No podrás revertir esto!<br>¿Deseas eliminar la plantilla <b>${template.name}</b>?`,
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Sí, ¡elimínala!',
        customClass: {
            confirmButton: 'btn bg-gradient-success',
            cancelButton: 'btn bg-gradient-danger'
        },
        buttonsStyling: false
    })

    if (result.isConfirmed) {
        try {
            await api.delete(`/${props.apiurl}/${id}`)
            const itemToDelete = allTemplates.value.find(item => item.id === id)
            if (itemToDelete) {
                itemToDelete.deleted_at = new Date().toISOString()
            }
            saveToLocalStorage(allTemplates.value)
            updateTemplates()
            proxy.$notify('Plantilla eliminada exitosamente', '', 'success', 'ni ni-like-2 ni-lg')
        } catch (error) {
            console.error('Error deleting template:', error)
            proxy.$notify('Error al eliminar plantilla', '', 'danger', 'ni ni-fat-remove ni-lg')
        }
    }
}

const resetEditState = () => {
    newTemplateName.value = ''
    localEditorContent.value = ''
    isEditing.value = false
    editingTemplateId.value = null
}

// Scroll infinito
const onScroll = async () => {
    if (scrollableOptions.value) {
        const { scrollTop, scrollHeight, clientHeight } = scrollableOptions.value
        if (scrollTop + clientHeight >= scrollHeight - 10 && currentPage.value < totalPages.value) {
            currentPage.value += 1
            await fetchAllTemplates(currentPage.value)
        }
    }
}

// Watchers
watch(searchQuery, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        if (newValue) {
            currentPage.value = 1
            fetchAllTemplates()
        } else {
            updateTemplates()
        }
    }
})

onMounted(() => {
    const localData = loadFromLocalStorage()
    if (localData && localData.length > 0) {
        allTemplates.value = localData
        updateTemplates()
    }
})
</script>

<style scoped>
/* Reutilizando los estilos del MedicineSelectorModal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 1rem;
}

.modal-container {
    background: white;
    border-radius: 20px;
    width: 100%;
    max-width: 900px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.modal-header {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    border-radius: 20px 20px 0 0;
    padding: 1.75rem 2rem;
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    overflow: hidden;
}

.modal-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/></svg>') repeat;
    background-size: 20px 20px;
    pointer-events: none;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: relative;
    z-index: 1;
}

.icon-wrapper {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    border: 1.5px solid rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(10px);
}

.modal-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.modal-subtitle {
    margin: 0.25rem 0 0 0;
    opacity: 0.95;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.92);
}

.close-btn {
    position: relative;
    z-index: 1;
    background: rgba(255, 255, 255, 0.2);
    border: 1.5px solid rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.close-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
}

.search-section {
    padding: 1.25rem 2rem 1rem 2rem;
    background: linear-gradient(135deg, #f7fafc 0%, #ffffff 100%);
    border-bottom: 1px solid #e2e8f0;
    transition: all 0.3s ease;
}

.show-form-section {
    margin-top: 0.75rem;
}

.search-wrapper {
    position: relative;
    margin-bottom: 0.75rem;
}

.search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #3b82f6;
    font-size: 1.1rem;
}

.search-input {
    width: 100%;
    padding: 0.875rem 3rem 0.875rem 3rem;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.search-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.clear-search-btn {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    background: #e2e8f0;
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    cursor: pointer;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.clear-search-btn:hover {
    background: #cbd5e1;
    color: #475569;
}

.alphabet-section {
    margin-top: 0.75rem;
}

.alphabet-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    justify-content: center;
    padding: 0;
}

.alphabet-btn {
    min-width: 26px;
    height: 26px;
    padding: 0.15rem 0.3rem;
    background: white;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;
}

.alphabet-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.05);
}

.alphabet-btn.active {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    border-color: #3b82f6;
    color: white;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
    transform: scale(1.05);
}

.templates-section {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
}

.templates-list {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 2rem;
}

.templates-list::-webkit-scrollbar {
    width: 8px;
}

.templates-list::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.templates-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.templates-list::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.template-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    margin-bottom: 0.5rem;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.template-card:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    transform: translateY(-2px);
}

.template-card.selected {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
    border-color: #3b82f6;
}

.template-content {
    flex: 1;
    min-width: 0;
}

.template-name {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    color: #1e293b;
    margin-bottom: 0.25rem;
}

.template-name i {
    color: #3b82f6;
}

.template-preview {
    font-size: 0.8rem;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
}

.template-actions {
    display: flex;
    gap: 0.5rem;
    margin-left: 1rem;
}

.action-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.85rem;
}

.use-btn {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
}

.use-btn:hover {
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
    transform: translateY(-2px);
}

.edit-btn {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
}

.edit-btn:hover {
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
    transform: translateY(-2px);
}

.delete-btn {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
}

.delete-btn:hover {
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
    transform: translateY(-2px);
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    text-align: center;
}

.empty-icon {
    font-size: 4rem;
    color: #cbd5e1;
    margin-bottom: 1rem;
}

.empty-text {
    font-size: 1.25rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.5rem;
}

.empty-subtext {
    font-size: 0.95rem;
    color: #94a3b8;
}

.loading-more {
    text-align: center;
    padding: 1rem;
    color: #3b82f6;
    font-weight: 500;
}

.form-section-full {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.form-header-full {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    border-bottom: 2px solid #3b82f6;
}

.form-header-left {
    display: flex;
    align-items: center;
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
}

.form-header-left i {
    color: #3b82f6;
    font-size: 1.4rem;
}

.back-btn {
    display: flex;
    align-items: center;
    padding: 0.65rem 1.25rem;
    background: white;
    border: 2px solid #3b82f6;
    border-radius: 10px;
    color: #3b82f6;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.3s ease;
}

.back-btn:hover {
    background: #3b82f6;
    color: white;
    transform: translateX(-3px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.form-content {
    flex: 1;
    overflow-y: auto;
    padding: 2rem;
    background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
}

.form-content::-webkit-scrollbar {
    width: 8px;
}

.form-content::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.form-content::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.form-content::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.form-group {
    display: flex;
    flex-direction: column;
}

.form-group.full-width {
    grid-column: 1 / -1;
}

.form-label {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.65rem;
}

.form-label i {
    color: #3b82f6;
    font-size: 1.1rem;
}

.required-star {
    color: #ef4444;
    margin-left: 0.25rem;
    font-weight: 700;
}

.form-input {
    padding: 1rem 1.25rem;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: white;
}

.form-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
}

.editor-wrapper-modal {
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    background: white;
    transition: all 0.3s ease;
}

.editor-wrapper-modal:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
}

.editor-modal {
    min-height: 300px;
}

.form-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    padding-top: 1rem;
    border-top: 2px solid #e2e8f0;
}

.modal-footer {
    padding: 1rem 2rem;
    background: #f8fafc;
    border-radius: 0 0 20px 20px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-active .modal-container {
    animation: modalSlideIn 0.3s ease;
}

.modal-fade-leave-active .modal-container {
    animation: modalSlideOut 0.3s ease;
}

@keyframes modalSlideOut {
    from {
        opacity: 1;
        transform: translateY(0) scale(1);
    }

    to {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }
}

/* Transición suave para fade simple */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Transición suave para lista de plantillas */
.list-fade-enter-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.list-fade-leave-active {
    transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);
    position: absolute;
}

.list-fade-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.list-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.list-fade-move {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-active {
    animation: slideInFromBottom 0.25s ease-out;
}

.slide-fade-leave-active {
    animation: slideOutToBottom 0.2s ease-in;
}

@keyframes slideInFromBottom {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideOutToBottom {
    from {
        opacity: 1;
        transform: translateY(0);
    }

    to {
        opacity: 0;
        transform: translateY(20px);
    }
}

.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.2s ease;
}

.slide-up-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.slide-up-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

.list-item-enter-active,
.list-item-leave-active {
    transition: all 0.2s ease;
}

.list-item-enter-from {
    opacity: 0;
    transform: scale(0.95);
}

.list-item-leave-to {
    opacity: 0;
    transform: scale(0.95);
}

@media (max-width: 768px) {
    .modal-container {
        max-width: 100%;
        max-height: 100vh;
        border-radius: 0;
    }

    .modal-header {
        border-radius: 0;
        padding: 1.25rem 1rem;
    }

    .modal-footer {
        padding: 1rem;
    }

    .modal-footer button {
        width: 100%;
    }

    .search-section {
        padding: 1rem;
    }

    .templates-list {
        padding: 1rem;
    }

    .form-content {
        padding: 1.5rem 1rem;
    }

    .alphabet-buttons {
        justify-content: center;
        gap: 0.25rem;
    }

    .alphabet-btn {
        min-width: 24px;
        height: 24px;
        font-size: 0.7rem;
        padding: 0.1rem 0.25rem;
    }

    .form-header-full {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
    }

    .form-header-left {
        justify-content: center;
    }

    .back-btn {
        width: 100%;
        justify-content: center;
    }

    .form-actions {
        flex-direction: column;
    }

    .form-actions>* {
        width: 100%;
    }
}
</style>
